import { CartComponentBase } from '@utilities/cart-component-base';
import { EVENTS } from '@utilities/events';
import { formatMoneyFromCurrency } from '@utilities/theme';

if (!customElements.get("cart-tiered-spend-banner")) {
  /**
   * Cart Tiered Spend Banner Custom Web Component
   *
   * Displays dynamic tiered spend messaging based on cart subtotal.
   * Shows progress through spend tiers and contextual reward messaging.
   *
   * This is a text-only display component -- it does not apply discounts or
   * add items. It resolves template tokens against tier data and the current
   * cart subtotal so merchants can communicate any kind of spend-based reward
   * (gift cards, free products, discounts handled elsewhere, etc.).
   *
   * Each tier defines a spend range via thresholdMin (required) and
   * thresholdMax (optional). When thresholdMax is 0 or absent the tier is
   * open-ended (e.g. "$500+").
   *
   * Template tokens available in banner message strings:
   *   {thresholdMin}        - Formatted minimum threshold of the relevant tier
   *   {thresholdMax}        - Formatted maximum threshold (empty string if not set)
   *   {nextThresholdMin}    - Formatted minimum threshold of the next tier
   *   {nextThresholdMax}    - Formatted maximum threshold of the next tier (empty string if not set)
   *   {thresholdDifference} - Formatted monetary amount needed to reach the next tier
   *   {reward}              - Reward description of the current (or first) tier
   *   {rewardMoney}         - Formatted monetary reward amount inside a tier reward string
   *   {nextReward}          - Reward description of the next tier
   *
   * Config shape (via component-config JSON):
   *   {
   *     "id": "...",
   *     "cartInclusionsSubtotal": 0,
   *     "strings": {
   *       "cartTiedSpendInitial":  "<p>...</p>",
   *       "cartTiedSpendMet":      "<p>...</p>",
   *       "cartTiedSpendMaximum":  "<p>...</p>"
   *     },
   *     "tiers": [
   *       { "id": "...", "thresholdMin": 20000, "thresholdMax": 34900, "reward": "$25 Gift Card" },
   *       { "id": "...", "thresholdMin": 35000, "thresholdMax": 0, "reward": "$50 Gift Card" }
   *     ]
   *   }
   *
   * @class CartTieredSpendBanner
   * @extends CartComponentBase
   */
  class CartTieredSpendBanner extends CartComponentBase {
    /**
     * Creates an instance of CartTieredSpendBanner
     * Initializes component state and configuration
     *
     * @constructor
     */
    constructor() {
      super();

      /**
       * Cart subtotal excluding certain items
       * @type {number}
       */
      this.cartSubtotal = 0;

      /**
       * Active currency code from cart state
       * @type {string}
       */
      this.currency = 'USD';

      // Bind event handlers to maintain context
      this.handleStateChange = this.handleStateChange.bind(this);
    }

    /**
     * Override the base class component-specific initialization
     * @protected
     */
    async _initializeComponent() {
      try {
        // Wait for cart state manager to be ready
        await this.waitForCartStateManager();
      } catch (error) {
        if (this.allowDebug()) {
          console.warn("CartTieredSpendBanner: Cart state manager not ready, proceeding with fallback");
        }
      }

      // Initialize cart subtotal from state manager
      await this.#initializeCartSubtotal();

      if (this.allowDebug()) {
        console.log(
          `CartTieredSpendBanner: Cart subtotal (excluding items): ${formatMoneyFromCurrency(
            this.cartSubtotal,
            this.currency
          )}`
        );
      }

      // Render the banner content
      await this.#renderBannerContent();

      if (this.allowDebug()) {
        console.log("CartTieredSpendBanner: Component initialized successfully");
      }
    }

    /**
     * Override the base class event listener setup
     * @protected
     */
    _setupEventListeners() {
      this._subscribe(EVENTS.CART_STATE_CHANGED, this.handleStateChange);
      this._subscribe(EVENTS.CART_STATE_REFRESHED, this.#handleStateRefreshed.bind(this));
    }

    /**
     * Initialize cart subtotal from state manager
     * @private
     */
    async #initializeCartSubtotal() {
      const stateManager = this.cartStateManager;
      if (!stateManager) {
        if (this.allowDebug()) {
          console.warn("CartTieredSpendBanner: CartStateManager not available yet");
        }
        this.cartSubtotal = 0;
        return;
      }

      const state = stateManager.getState();
      this.cartSubtotal = state.inclusionsOnlySubtotal || 0;
      this.currency = state.currency || 'USD';

      if (this.allowDebug()) {
        console.log(
          "CartTieredSpendBanner: Using state manager subtotal:",
          this.cartSubtotal
        );
      }
    }

    /**
     * Handle cart state changes from the state manager
     * @param {Object} data - The cart state change data
     */
    async handleStateChange(data) {
      const { state, changes } = data;

      if (this.allowDebug()) {
        console.log("CartTieredSpendBanner: Received state change:", {
          state,
          changes,
          newSubtotal: state.inclusionsOnlySubtotal,
        });
      }

      // Update currency from state
      if (state.currency) {
        this.currency = state.currency;
      }

      // Update cart subtotal if it changed
      const newSubtotal = state.inclusionsOnlySubtotal || 0;
      if (newSubtotal !== this.cartSubtotal) {
        await this.updateCartSubtotal(newSubtotal);
      }
    }

    /**
     * Handle cart state refreshed events
     * Forces a full tier recalculation and re-render
     * @private
     */
    async #handleStateRefreshed() {
      if (this.allowDebug()) {
        console.log("CartTieredSpendBanner: Cart state refreshed, forcing full tier recalculation and re-render");
      }

      try {
        // Re-initialize cart subtotal from state manager
        await this.#initializeCartSubtotal();

        // Force a full tier recalculation and banner re-render
        await this.#renderBannerContent();
      } catch (error) {
        if (this.allowDebug()) {
          console.warn("CartTieredSpendBanner: Error handling state refresh:", error);
        }
      }
    }

    /**
     * Override base class element caching to include component-specific elements
     * @protected
     */
    _cacheElements() {
      // Call base class caching first
      super._cacheElements();

      // Cache component-specific elements
      this.cartDrawer = this.closest("cart-drawer");
    }

    /**
     * Handles component errors gracefully
     * @param {string} message - Error message to display
     * @private
     */
    #handleError(message) {
      this._handleError(new Error(message), 'component error', true);

      // Remove the component if it encounters an error
      this.remove();
    }

    /**
     * Renders the tiered spend banner content based on configuration
     * @returns {Promise<boolean>} Whether content was successfully rendered
     * @private
     */
    async #renderBannerContent() {
      try {
        // Validate configuration
        if (!this.config || !this.config.tiers || !Array.isArray(this.config.tiers) || this.config.tiers.length === 0) {
          if (this.allowDebug()) {
            console.log(
              "CartTieredSpendBanner: No valid tiers configuration found, removing component"
            );
          }
          this.remove();
          return false;
        }

        const content = this.#renderTieredSpendContent();

        // Render the content
        this.innerHTML = content;

        return true;
      } catch (error) {
        this._handleError(error, 'rendering banner content', true);
        this.#handleError("Failed to render banner content");
        return false;
      }
    }

    /**
     * Removes the component from the DOM
     * Dispatches removal event for other components to handle
     */
    remove() {
      if (this.allowDebug()) {
        console.log("CartTieredSpendBanner: Removing component");
      }

      super.remove();
    }

    /**
     * Renders tiered spend banner content by resolving template tokens
     * against the current tier state.
     *
     * Three message states:
     *   1. Initial  -- no tier met yet; shows first tier goal
     *   2. Met      -- at least one tier met, higher tier still available
     *   3. Maximum  -- highest tier reached
     *
     * @returns {string} HTML content for the banner
     * @private
     */
    #renderTieredSpendContent() {
      const tiers = this.#sortTiersByThreshold(this.config.tiers);
      const currentTierIndex = this.#findCurrentTierIndex(tiers, this.cartSubtotal);
      const currentTier = tiers[currentTierIndex];
      const nextTier = tiers[currentTierIndex + 1];

      if (this.allowDebug()) {
        console.log("CartTieredSpendBanner: Tier calculation:", {
          tiers,
          currentTierIndex,
          currentTier,
          nextTier,
          cartSubtotal: this.cartSubtotal,
        });
      }

      if (currentTierIndex === -1) {
        // ── Initial: No tiers met yet ──
        const firstTier = tiers[0];
        const difference = firstTier.thresholdMin - this.cartSubtotal;

        return this.#resolveTokens(this.config.strings.cartTiedSpendInitial, {
          thresholdMin: formatMoneyFromCurrency(firstTier.thresholdMin, this.currency),
          thresholdMax: this.#formatThresholdMax(firstTier.thresholdMax),
          reward: this.#resolveRewardMoneyToken(firstTier),
          thresholdDifference: formatMoneyFromCurrency(difference, this.currency),
        });
      }

      if (nextTier) {
        // ── Met: Current tier reached, higher tier available ──
        const difference = nextTier.thresholdMin - this.cartSubtotal;

        return this.#resolveTokens(this.config.strings.cartTiedSpendMet, {
          thresholdMin: formatMoneyFromCurrency(currentTier.thresholdMin, this.currency),
          thresholdMax: this.#formatThresholdMax(currentTier.thresholdMax),
          reward: this.#resolveRewardMoneyToken(currentTier),
          nextThresholdMin: formatMoneyFromCurrency(nextTier.thresholdMin, this.currency),
          nextThresholdMax: this.#formatThresholdMax(nextTier.thresholdMax),
          nextReward: this.#resolveRewardMoneyToken(nextTier),
          thresholdDifference: formatMoneyFromCurrency(difference, this.currency),
        });
      }

      // ── Maximum: Highest tier reached ──
      return this.#resolveTokens(this.config.strings.cartTiedSpendMaximum, {
        thresholdMin: formatMoneyFromCurrency(currentTier.thresholdMin, this.currency),
        thresholdMax: this.#formatThresholdMax(currentTier.thresholdMax),
        reward: this.#resolveRewardMoneyToken(currentTier),
      });
    }

    /**
     * Replaces {rewardMoney} inside a tier reward string.
     *
     * @param {Object} tier - Tier object containing reward and rewardMoney values
     * @returns {string} Reward string with {rewardMoney} resolved
     * @private
     */
    #resolveRewardMoneyToken(tier) {
      const rewardTemplate = tier?.reward || '';
      const rewardMoney = tier?.rewardMoney || '';

      return rewardTemplate.replace(/\{rewardMoney\}/g, rewardMoney);
    }

    /**
     * Formats a thresholdMax value as money, or returns an empty string
     * when the tier is open-ended (thresholdMax is 0, null, or undefined).
     *
     * @param {number|null|undefined} thresholdMax - The max threshold value
     * @returns {string} Formatted money string or empty string
     * @private
     */
    #formatThresholdMax(thresholdMax) {
      if (!thresholdMax || thresholdMax <= 0) return '';
      return formatMoneyFromCurrency(thresholdMax, this.currency);
    }

    /**
     * Replaces all recognised template tokens in a string.
     * Tokens use the format {tokenName}.
     *
     * @param {string} template - The template string containing {tokens}
     * @param {Object} values   - Key/value map of token names to replacement values
     * @returns {string} The template with all matching tokens replaced
     * @private
     */
    #resolveTokens(template, values) {
      if (!template) return '';

      let result = template;
      for (const [token, value] of Object.entries(values)) {
        result = result.replace(new RegExp(`\\{${token}\\}`, 'g'), value);
      }
      return result;
    }

    /**
     * Sorts tiers by thresholdMin in ascending order
     * @param {Array} tiers - Array of tier objects
     * @returns {Array} Sorted array of tiers
     * @private
     */
    #sortTiersByThreshold(tiers) {
      return [...tiers].sort((a, b) => a.thresholdMin - b.thresholdMin);
    }

    /**
     * Finds the highest tier that the cart subtotal has reached.
     * A tier is reached when subtotal >= tier.thresholdMin.
     *
     * @param {Array} sortedTiers - Array of tiers sorted by thresholdMin
     * @param {number} subtotal - Current cart subtotal
     * @returns {number} Index of the current tier (-1 if no tier reached)
     * @private
     */
    #findCurrentTierIndex(sortedTiers, subtotal) {
      for (let i = sortedTiers.length - 1; i >= 0; i--) {
        if (subtotal >= sortedTiers[i].thresholdMin) {
          return i;
        }
      }
      return -1;
    }

    /**
     * Updates the cart subtotal and re-renders content
     * @param {number} newSubtotal - New cart subtotal value
     * @public
     */
    async updateCartSubtotal(newSubtotal) {
      if (this.allowDebug()) {
        console.log(
          `CartTieredSpendBanner: Updating cart subtotal from ${this.cartSubtotal} to ${newSubtotal}`
        );
      }

      this.cartSubtotal = newSubtotal;
      await this.#renderBannerContent();
    }

    /**
     * Gets the current cart subtotal
     * @returns {number} Current cart subtotal
     * @public
     */
    getCartSubtotal() {
      return this.cartSubtotal;
    }

    /**
     * Gets the current configuration
     * @returns {Object} Current component configuration
     * @public
     */
    getConfig() {
      return this.config;
    }
  }

  customElements.define("cart-tiered-spend-banner", CartTieredSpendBanner);
}
